import { CrudService } from "@/shared/core/services/crudService";
import { uploadToBunny } from "./bunnyTusUpload";

const BunnyFailed = [5, 6];
const PollFastMs = 5000;
const PollSlowMs = 30000;
const PollFastForMs = 60000;
const PollMaxMs = 15 * 60000;
export const MaxVideoMinutes = 10;
export const MaxVideoGb = 2;
const MaxVideoSeconds = MaxVideoMinutes * 60;
const MaxVideoBytes = MaxVideoGb * 1024 * 1024 * 1024;

function videoSeconds(file: File): Promise<number> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration || 0);
    };
    video.onerror = () => resolve(0);
    video.src = URL.createObjectURL(file);
  });
}

export interface VideoSubmissionEndpoints {
  get: string;
  createVideo: string;
  submit: string;
  videoStatus: string;
}

// What the customer sent back on a piece of content and its review, shared by the video and the panel components.
export class VideoSubmissionState {
  submission: any = null;
  playUrl: string = "";
  comments: any[] = [];
  videoDuration: number = 0;
  activeCommentSecond: number = -1;
  text: string = "";
  sending: boolean = false;
  uploading: boolean = false;
  uploadPercent: number = 0;
  uploadDone: boolean = false;
  justUploaded: boolean = false;
  videoFailed: boolean = false;
  overLimits: boolean = false;

  private pollTimer: any = null;
  private pollStarted: number = 0;
  private retries: number = 0;

  constructor(private endpoints: VideoSubmissionEndpoints, private body: any, private hasSession: () => boolean) {}

  async load() {
    if (!this.hasSession()) {
      if (this.retries < 5) {
        this.retries++;
        setTimeout(() => this.load(), 600);
      }
      return;
    }
    const data = await new CrudService().httpGet(this.endpoints.get);
    if (!data || !data.ok) {
      if (this.retries < 3) {
        this.retries++;
        setTimeout(() => this.load(), 1000);
      }
      return;
    }
    this.submission = data.submission;
    this.playUrl = data.playUrl || "";
    this.comments = data.comments || [];
    this.videoDuration = data.videoDuration || 0;
  }

  async sendComment() {
    this.sending = true;
    const res = await new CrudService().httpPost(this.endpoints.submit, { ...this.body, text: this.text });
    this.sending = false;
    if (res && res.ok) {
      this.uploadDone = true;
      await this.load();
    }
  }

  async uploadVideo(file: File) {
    this.overLimits = file.size > MaxVideoBytes || (await videoSeconds(file)) > MaxVideoSeconds + 1;
    if (this.overLimits) return;
    this.uploading = true;
    this.uploadPercent = 0;
    const crud = new CrudService();

    const created = await crud.httpPost(this.endpoints.createVideo, this.body);
    if (!created || !created.ok) {
      this.uploading = false;
      return;
    }

    const upload = created.upload;
    if (!(await uploadToBunny(file, upload, (p) => (this.uploadPercent = p)))) {
      this.uploading = false;
      return;
    }

    const sent = await crud.httpPost(this.endpoints.submit, { ...this.body, videoId: upload.videoId, text: this.text });
    this.uploading = false;
    if (sent && sent.ok) {
      this.uploadDone = true;
      this.justUploaded = true;
      await this.load();
      this.pollVideoReady(upload.videoId);
    }
  }

  seek(second: number) {
    if (second == null || !this.playUrl) return;
    this.activeCommentSecond = second;
    const base = this.playUrl.replace(/[&?]t=\d+/g, "");
    this.playUrl = base + (base.includes("?") ? "&" : "?") + "t=" + second;
  }

  dispose() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
    this.pollStarted = 0;
  }

  // Asks often while Bunny usually finishes, then less; stops when the video is ready, has failed or after a while.
  private pollVideoReady(videoId: string) {
    this.dispose();
    const started = Date.now();
    this.pollStarted = started;
    const tick = async () => {
      const data = await new CrudService().httpGet(`${this.endpoints.videoStatus}/${videoId}`);
      if (this.pollStarted !== started) return;
      if (data && data.ready) {
        this.justUploaded = false;
        this.dispose();
        await this.load();
        return;
      }
      if (data && data.rejected) {
        this.justUploaded = false;
        this.uploadDone = false;
        this.overLimits = true;
        this.dispose();
        await this.load();
        return;
      }
      if (data && BunnyFailed.indexOf(data.status) >= 0) {
        this.justUploaded = false;
        this.videoFailed = true;
        this.dispose();
        return;
      }
      const elapsed = Date.now() - started;
      if (elapsed >= PollMaxMs) {
        this.dispose();
        return;
      }
      this.pollTimer = setTimeout(tick, elapsed < PollFastForMs ? PollFastMs : PollSlowMs);
    };
    this.pollTimer = setTimeout(tick, PollFastMs);
  }
}

export function fmtSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m + ":" + (sec < 10 ? "0" : "") + sec;
}
